import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, ZoomControl, Polyline, useMapEvents, useMap } from 'react-leaflet';
import { useLocation, useNavigate } from 'react-router-dom';
import { HAZARDS } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import { Crosshair, AlertTriangle, Navigation, Check, X, MapPin, ShieldCheck, Zap, Car, ArrowRight } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import '../styles/global.css';
import L from 'leaflet';

// Fix Leaflet icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

// --- AUTO ZOOM HANDLER ---
const AutoZoomHandler = () => {
  const map = useMap();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.focusLat && location.state?.focusLng) {
      const { focusLat, focusLng, focusZoom } = location.state;
      map.flyTo([focusLat, focusLng], focusZoom || 16, {
        animate: true,
        duration: 1.5 
      });
    }
  }, [location, map]);

  return null;
};

const LiveMap = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [map, setMap] = useState(null);
  const [mode, setMode] = useState('view');
  const [userLocation, setUserLocation] = useState(null);
  
  // Jaipur Default Coordinates
  const JAIPUR_CENTER = [26.9124, 75.7873];
  
  const [start, setStart] = useState('LNMIIT, Jaipur');
  const [end, setEnd] = useState('Raja Park, Jaipur');
  const [isRouting, setIsRouting] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedPos, setSelectedPos] = useState(null);
  
  const isSelectMode = location.state?.selectMode;

  const mockRoutes = [
    {
      id: 1, type: 'Safest', color: '#10b981', icon: <ShieldCheck size={18} />,
      duration: '45 min', distance: '18 km', riskLevel: 'Low',
      description: 'Avoids Agra Road construction.',
      path: [[26.9124, 75.7873], [26.9200, 75.8000], [26.9350, 75.8200], [26.8900, 75.8500]]
    },
    {
      id: 2, type: 'Fastest (High Risk)', color: '#ef4444', icon: <Zap size={18} />,
      duration: '35 min', distance: '16.5 km', riskLevel: 'High',
      description: 'Heavy traffic near Transport Nagar.',
      path: [[26.9124, 75.7873], [26.9000, 75.8100], [26.8800, 75.8300], [26.8900, 75.8500]]
    },
    {
      id: 3, type: 'Smooth Traffic', color: '#3b82f6', icon: <Car size={18} />,
      duration: '40 min', distance: '19 km', riskLevel: 'Moderate',
      description: 'Via Delhi Highway.',
      path: [[26.9124, 75.7873], [26.9500, 75.7500], [26.9200, 75.8500], [26.8900, 75.8500]]
    }
  ];

  const handleRouteSearch = (e) => {
    e.preventDefault();
    if (!start || !end) return alert("Please enter locations.");
    setIsRouting(true);
    setRoutes([]);
    setTimeout(() => {
      setRoutes(mockRoutes);
      setSelectedRouteId(1);
      setIsRouting(false);
    }, 1500);
  };

  const handleLocate = () => {
    if (!map) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const newPos = { lat: latitude, lng: longitude };
        map.flyTo([latitude, longitude], 15, { animate: true });
        setUserLocation(newPos);
      },
      (error) => alert("Could not access location.")
    );
  };

  const handleConfirmLocation = async () => {
    if (!selectedPos) return;
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${selectedPos.lat}&lon=${selectedPos.lng}`);
      const data = await response.json();
      const address = data.display_name || `${selectedPos.lat}, ${selectedPos.lng}`;
      navigate('/report', { state: { selectedAddress: address } });
    } catch (e) {
      navigate('/report', { state: { selectedAddress: `${selectedPos.lat}, ${selectedPos.lng}` } });
    }
  };

  const activeRoute = routes.find(r => r.id === selectedRouteId);
  const tileLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  return (
    <div style={{ 
      height: 'calc(100vh - 70px)', 
      width: '100vw', 
      position: 'relative', 
      marginTop: '70px', 
      overflow: 'hidden' 
    }}>
      
      {theme === 'dark' && (
        <style>
          {`
            .dark-mode-tiles {
              filter: invert(100%) hue-rotate(180deg) brightness(95%) contrast(90%);
            }
          `}
        </style>
      )}

      {/* 1. PIN SELECTION OVERLAY */}
      {isSelectMode && (
        <div className="glass-panel" style={{
          position: 'absolute', top: 30, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1000, padding: '15px 25px', display: 'flex', gap: '15px', alignItems: 'center',
          border: '2px solid #10b981', boxShadow: '0 0 20px rgba(16,185,129,0.3)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: 0, color: '#10b981' }}>Tap map to place pin</h4>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{selectedPos ? "Location Selected!" : "Waiting for input..."}</div>
          </div>
          {selectedPos && (
            <button onClick={handleConfirmLocation} className="btn-primary" style={{ padding: '8px 16px', fontSize: '0.9rem', display:'flex', alignItems:'center', gap:'5px' }}>
              <Check size={16}/> Confirm
            </button>
          )}
        </div>
      )}

      {/* 2. SIDEBAR */}
      {!isSelectMode && (
        <div className="glass-panel" style={{ 
          position: 'absolute', top: 20, left: 20, zIndex: 900, 
          padding: '20px', width: '380px', maxWidth: '90%', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', gap: '15px', overflowY: 'auto',
          transition: 'all 0.3s ease'
        }}>
          {mode === 'view' && (
            <>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{width:10, height:10, background:'#ef4444', borderRadius:'50%', display:'inline-block'}}></span>
                  Live Road Hazards
                </h3>
                <p style={{ margin: '4px 0 0 18px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Real-time crowd intelligence</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                <button onClick={() => navigate('/report')} className="btn-primary" style={{ flex: 1, padding: '12px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <AlertTriangle size={18} /> Report
                </button>
                <button onClick={() => setMode('route')} className="glass-btn" style={{ flex: 1, padding: '12px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <Navigation size={18} /> Safe Route
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'5px' }}>
                <div style={{ background:'rgba(239,68,68,0.1)', padding:'10px', borderRadius:'8px', textAlign:'center' }}>
                  <div style={{color:'#ef4444', fontWeight:'bold', fontSize:'1.2rem'}}>12</div>
                  <div style={{fontSize:'0.7rem', opacity:0.7}}>Critical Hazards</div>
                </div>
                <div style={{ background:'rgba(245,158,11,0.1)', padding:'10px', borderRadius:'8px', textAlign:'center' }}>
                  <div style={{color:'#f59e0b', fontWeight:'bold', fontSize:'1.2rem'}}>24</div>
                  <div style={{fontSize:'0.7rem', opacity:0.7}}>Moderate Warnings</div>
                </div>
              </div>
            </>
          )}

          {mode === 'route' && (
            <>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <h3 style={{ fontSize: '1.2rem', display:'flex', alignItems:'center', gap:'10px' }}>
                  <Navigation size={20} color="#3b82f6"/> Plan Route
                </h3>
                <button onClick={() => {setMode('view'); setRoutes([]);}} style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--text-muted)' }}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleRouteSearch} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ position: 'relative' }}>
                   <div style={{ position: 'absolute', left: '12px', top: '14px', width: '8px', height: '8px', borderRadius: '50%', border: '2px solid #3b82f6' }}></div>
                   <input type="text" placeholder="Start" value={start} onChange={e=>setStart(e.target.value)} style={{ paddingLeft: '30px' }} />
                </div>
                <div style={{ position: 'relative' }}>
                   <MapPin size={16} color="#ef4444" style={{ position: 'absolute', left: '8px', top: '12px' }} />
                   <input type="text" placeholder="Destination" value={end} onChange={e=>setEnd(e.target.value)} style={{ paddingLeft: '30px' }} />
                </div>
                <button type="submit" className="btn-primary" disabled={isRouting} style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'8px' }}>
                  {isRouting ? "Calculating..." : "Find Routes"} {!isRouting && <ArrowRight size={16}/>}
                </button>
              </form>
              {routes.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginTop:'5px' }}>
                  <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Select a path logic:</div>
                  {routes.map(r => (
                    <div key={r.id} onClick={() => setSelectedRouteId(r.id)} 
                      style={{
                        padding:'12px', borderRadius:'10px', cursor:'pointer',
                        border: selectedRouteId === r.id ? `2px solid ${r.color}` : '1px solid var(--btn-border)',
                        background: selectedRouteId === r.id ? `${r.color}10` : 'transparent'
                      }}
                    >
                      <div style={{ display:'flex', justifyContent:'space-between', fontWeight:'bold', fontSize:'0.9rem', color: r.color, marginBottom:'4px' }}>
                        <span style={{display:'flex', gap:'5px', alignItems:'center'}}>{r.icon} {r.type}</span>
                        <span>{r.duration}</span>
                      </div>
                      <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{r.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* LOCATE BUTTON - FIXED POSITION & Z-INDEX */}
      <button onClick={handleLocate} className="glass-panel" style={{
        position: 'absolute', 
        bottom: '90px',  // <--- FIXED: Moved up to clear Zoom Controls
        right: '12px', 
        zIndex: 9999,    // <--- FIXED: Boosted z-index
        width: '45px', 
        height: '45px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        cursor: 'pointer', 
        padding: 0
      }}>
        <Crosshair color="#10b981" size={24} /> {/* FIXED: Hardcoded Green Color */}
      </button>

      {/* MAP */}
      <MapContainer 
        center={JAIPUR_CENTER} 
        zoom={12} 
        zoomControl={false} 
        ref={setMap} 
        style={{ height: '100%', width: '100%' }}
      >
        <AutoZoomHandler /> 

        <ZoomControl position="bottomright" />
        
        <TileLayer 
          attribution='&copy; OpenStreetMap' 
          url={tileLayerUrl} 
          className={theme === 'dark' ? 'dark-mode-tiles' : ''}
        />
        
        {/* EVENT HANDLERS */}
        {isSelectMode && <MapEventsHandler onMapClick={setSelectedPos} />}
        
        {location.state?.focusLat && (
          <Marker position={[location.state.focusLat, location.state.focusLng]}>
            <Popup><strong>New Report!</strong><br/>Live Location</Popup>
            <Circle center={[location.state.focusLat, location.state.focusLng]} radius={300} pathOptions={{color: 'blue'}} />
          </Marker>
        )}

        {selectedPos && <Marker position={selectedPos} />}
        {userLocation && <Marker position={userLocation}><Popup>You are here</Popup></Marker>}

        {!isSelectMode && HAZARDS.map(h => (
          <Marker key={h.id} position={[h.lat, h.lng]}>
            <Popup><strong>{h.type}</strong><br/>{h.city}</Popup>
            <Circle center={[h.lat, h.lng]} radius={2000} pathOptions={{color: h.severity === 'High' ? 'red' : 'orange'}} />
          </Marker>
        ))}

        {activeRoute && (
          <>
            <Polyline positions={activeRoute.path} pathOptions={{ color: activeRoute.color, weight: 6, opacity: 0.9 }} />
            {routes.filter(r => r.id !== activeRoute.id).map(r => (
               <Polyline key={r.id} positions={r.path} pathOptions={{ color: 'gray', weight: 4, opacity: 0.3, dashArray: '10, 10' }} eventHandlers={{ click: () => setSelectedRouteId(r.id) }} />
            ))}
            <Marker position={activeRoute.path[0]}><Popup>Start</Popup></Marker>
            <Marker position={activeRoute.path[activeRoute.path.length-1]}><Popup>End</Popup></Marker>
          </>
        )}

      </MapContainer>
    </div>
  );
};

// Helper for map events
const MapEventsHandler = ({ onMapClick }) => {
  useMapEvents({ click(e) { if (onMapClick) onMapClick(e.latlng); } });
  return null;
};

export default LiveMap;